import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PROJECT_MEMBER_ROLES, routes } from 'src/app/constants';
import { removeUndefinedFields } from 'src/app/utils/utils';
import { SlackSurvey } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

import { UpsertSurveyModalComponent } from '../upsert-survey-modal/upsert-survey-modal.component';

@Component({
  selector: 'app-surveys',
  templateUrl: './surveys.component.html',
  styleUrls: ['./surveys.component.scss'],
})
export class SurveysComponent implements OnInit, OnDestroy {
  surveys: any[] = [];
  isLoading = false;
  ColumnMode = ColumnMode;
  routerSub: Subscription;
  isAnalyticsModalOpen = false;
  currentSurveyId?: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiService,
    private msgSrvc: MessageService,
    private analyticsService: AnalyticsService,
    private navController: NavController,
    private modalCtrl: ModalController,
    public authService: AuthService,
  ) {}

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(() => {
        this.loadSurveys();
      });

    await this.loadSurveys();

    this.currentSurveyId = this.route.snapshot.queryParamMap.get('surveyId');

    if (this.currentSurveyId) {
      this.isAnalyticsModalOpen = true;
    }
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async loadSurveys() {
    const queryParams: any = {
      where: this.authService.isTenantAdmin
        ? this.authService.selectedProject
          ? [
              {
                field: 'projectId',
                operator: '==',
                value: this.authService.selectedProject.id,
              },
            ]
          : []
        : [
            {
              field: 'projectId',
              operator: 'in',
              value: this.authService.projects
                .filter(
                  (p) =>
                    this.authService.isProjectOwner(p.id) &&
                    (!this.authService.selectedProject || p.id === this.authService.selectedProject.id),
                )
                .map((p) => p.id),
            },
          ],
    };

    try {
      const result: any =
        !this.authService.selectedProject || this.authService.isProjectOwner(this.authService.selectedProject.id)
          ? await this.apiService.postPromise(`/surveys/list`, queryParams)
          : {
              data: [],
            };

      this.surveys = result.data;
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  viewAnalytics(survey: any) {
    this.analyticsService.trackEvent('Surveys', 'View survey analytics', {
      surveyId: survey.id,
    });

    this.currentSurveyId = survey.id;
    this.isAnalyticsModalOpen = true;
  }

  send(survey: any) {
    this.analyticsService.trackEvent('Surveys', 'Send survey', {
      surveyId: survey.id,
    });

    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        previousUrl: this.location.path(),
        surveyId: survey.id,
      }),
    });
  }

  onDismissAnalyicsModal() {
    this.isAnalyticsModalOpen = false;
    this.currentSurveyId = undefined;
  }

  async openCreateSurveyModal() {
    const modal = await this.modalCtrl.create({
      component: UpsertSurveyModalComponent,
      componentProps: {},
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.loadSurveys();
    });
  }

  async openEditSurveyModal(survey: any) {
    const modal = await this.modalCtrl.create({
      component: UpsertSurveyModalComponent,
      componentProps: {
        survey: {
          ...survey,
          style: {
            ...survey.style,
          },
        },
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.loadSurveys();
    });
  }

  openHelpdeskArticle() {
    window.open('https://docs.unthread.io/engagement/surveys#analytics', '_blank');
  }

  getSurveyLabels(survey: SlackSurvey): string[] {
    const numbers = `1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 6️⃣ 7️⃣ 8️⃣ 9️⃣ 🔟`.split(' ');
    const emojis = `🤩 🙂 😐 😞 😡`.split(' ');
    if (survey.type === 'nps') {
      return numbers;
    }

    if (survey.style?.selectionOptions === 'emojiFaces') {
      return emojis;
    }

    return numbers.slice(0, 5);
  }
}
